import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Divider, Grid,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import api from '../api';

export default function Balances() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/financials/balances/')
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load balances.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} mb={3}>Balances</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Per-farmer totals */}
      {data?.farmer_totals && (
        <>
          <Typography variant="h6" fontWeight={600} mb={1}>Farmer Summary</Typography>
          <Grid container spacing={2} mb={4}>
            {Object.entries(data.farmer_totals).map(([name, totals]) => {
              const net = parseFloat(totals.net);
              return (
                <Grid item xs={12} sm={4} key={name}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" fontWeight={700}>{name}</Typography>
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">Paid Out</Typography>
                        <Typography variant="body2" fontWeight={600}>${totals.paid}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">Owed Share</Typography>
                        <Typography variant="body2" fontWeight={600}>${totals.owed}</Typography>
                      </Box>
                      <Divider sx={{ mb: 1 }} />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight={700}>Net</Typography>
                        <Chip
                          size="small"
                          label={`${net >= 0 ? '+' : ''}$${Math.abs(net).toFixed(2)}`}
                          color={net >= 0 ? 'success' : 'error'}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {net > 0 ? 'others owe this farmer' : net < 0 ? 'this farmer owes others' : 'settled up'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Who owes whom */}
      <Typography variant="h6" fontWeight={600} mb={1}>Who Owes Whom</Typography>
      {data?.balances?.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">All settled up — no outstanding balances.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>From</TableCell>
                <TableCell scope="col" />
                <TableCell scope="col" sx={{ fontWeight: 700 }}>To</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.balances?.map((b, i) => (
                <TableRow key={i} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{b.from}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <ArrowForwardIcon fontSize="small" color="action" />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{b.to}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={`$${parseFloat(b.amount).toFixed(2)}`} color="warning" size="small" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Prior-year carry-forwards and one-time adjustments */}
      {data?.adjustments?.length > 0 && (
        <Box mt={4}>
          <Typography variant="h6" fontWeight={600} mb={1}>Balance Adjustments</Typography>
          <Typography variant="body2" color="text.secondary" mb={1}>
            One-time carry-forwards and cash settlements included in the totals above.
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell scope="col" sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 700 }}>Description</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 700 }}>From</TableCell>
                  <TableCell scope="col" />
                  <TableCell scope="col" sx={{ fontWeight: 700 }}>To</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.adjustments.map((a, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{a.date}</TableCell>
                    <TableCell>{a.description}</TableCell>
                    <TableCell>{a.from}</TableCell>
                    <TableCell align="center">
                      <ArrowForwardIcon fontSize="small" color="action" />
                    </TableCell>
                    <TableCell>{a.to}</TableCell>
                    <TableCell align="right">
                      <Chip size="small" variant="outlined" label={`$${parseFloat(a.amount).toFixed(2)}`} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
